import { useMemo, useState } from 'react'
import { Check, Info, Plus, RefreshCw, Trash2, X } from 'lucide-react'
import {
  parsePhoneAssignError,
  useAddPhoneAssistant,
  useAdminOrgsProvisioning,
  useAdminPhoneNumbers,
  useAdminVapiAssistants,
  useAssignOrgPhone,
  useRemovePhoneAssistant,
  useSyncPhoneNumbers,
  type AdminOrgProvisioningRow,
  type AdminPhoneNumber,
  type PhoneAssistantKind,
} from '../../api/admin-phones'
import { OrgProviderControl } from '../../components/admin/OrgProviderControl'
import { RetellPhoneInventorySection } from '../../components/admin/RetellPhoneInventorySection'

/**
 * "Phones" tab — superadmin-only phone-number provisioning for both providers.
 *
 * Four sections (top-down):
 *   1. Environment notice — reminds the operator that stage and prod use
 *      different VAPI phone numbers and their ``serverUrl`` must point at
 *      THIS environment's webhook.
 *   2. Org provisioning matrix — one row per org with Stripe sub status,
 *      assigned phone, and inbound/outbound assistant coverage. Inline
 *      Assign / Unassign actions (modal for assign because we need a phone
 *      picker; inline confirm for unassign — matches OrgSubscriptionItemsPanel).
 *   3. VAPI phone inventory — sync-from-VAPI button + table of cached phones
 *      with an inline "Manage assistants" editor.
 *   4. Voice provider — per-org flip between ``vapi`` and ``retell``, wrapping
 *      ``POST /api/admin/orgs/{id}/provider``. Sits just above the Retell
 *      inventory because the usual flow is: flip an org to ``retell`` here,
 *      then provision its Retell phones below.
 *   5. Retell phone inventory — per-org Retell phone + agent provisioning,
 *      used after flipping an org's ``provider`` to ``retell``. Lives in a
 *      sibling component so the VAPI surface stays unchanged.
 *
 * Step-up MFA on every mutation is handled centrally in
 * ``ui/src/api/client.ts:43-49`` — a 401 with ``code=step_up_required``
 * navigates the browser through ``/auth/workos/step-up`` and back, so the
 * operator just re-clicks. Don't add optimistic UI on these mutations or it
 * will desync from the redirect.
 */
export function AdminBillingPhonesTab() {
  const phones = useAdminPhoneNumbers()
  const orgs = useAdminOrgsProvisioning()

  return (
    <div className="space-y-6">
      <EnvironmentNotice />
      <OrgProvisioningMatrix
        orgs={orgs.data?.orgs ?? []}
        isLoading={orgs.isLoading}
        phones={phones.data?.phones ?? []}
      />
      <PhoneInventoryPanel
        phones={phones.data?.phones ?? []}
        isLoading={phones.isLoading}
      />
      <OrgProviderControl />
      <RetellPhoneInventorySection />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Section 1 — Environment notice
// ─────────────────────────────────────────────────────────────────────────

function EnvironmentNotice() {
  // Render the webhook URL dynamically so the panel reads correctly on stage,
  // prod, and any local tunnel — operators copy/paste the line straight into
  // VAPI's dashboard.
  const webhookUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/vapi/webhook`
      : '/vapi/webhook'
  return (
    <section className="flex gap-3 rounded-lg border border-border bg-surface p-4 text-sm">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-secondary" />
      <div className="space-y-1">
        <div className="font-medium">Environment-specific phone numbers</div>
        <p className="text-text-secondary">
          Stage uses different VAPI phone numbers than prod. Confirm each
          phone&rsquo;s <code>serverUrl</code> in the VAPI dashboard points at
          this environment&rsquo;s webhook:
        </p>
        <code className="block break-all rounded border border-border bg-background px-2 py-1 font-mono text-xs">
          {webhookUrl}
        </code>
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Section 2 — Org provisioning matrix
// ─────────────────────────────────────────────────────────────────────────

interface OrgProvisioningMatrixProps {
  orgs: AdminOrgProvisioningRow[]
  isLoading: boolean
  phones: AdminPhoneNumber[]
}

function OrgProvisioningMatrix({
  orgs,
  isLoading,
  phones,
}: OrgProvisioningMatrixProps) {
  // Sort: unassigned orgs first (squeaky wheel), then by name. Mirrors the
  // operator workflow — they're usually here to provision the unassigned ones.
  const sorted = useMemo(
    () =>
      [...orgs].sort((a, b) => {
        const aUnassigned = a.vapi_phone_number_id == null
        const bUnassigned = b.vapi_phone_number_id == null
        if (aUnassigned !== bUnassigned) return aUnassigned ? -1 : 1
        return a.name.localeCompare(b.name)
      }),
    [orgs],
  )

  const [assigningOrg, setAssigningOrg] =
    useState<AdminOrgProvisioningRow | null>(null)
  const [confirmUnassign, setConfirmUnassign] = useState<string | null>(null)

  const assignMutation = useAssignOrgPhone()

  async function doUnassign(orgId: string) {
    try {
      await assignMutation.mutateAsync({ orgId, phone_id: null })
      setConfirmUnassign(null)
    } catch {
      // surfaces via assignMutation.error
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="font-medium">Org provisioning</div>
        <div className="text-xs text-text-secondary">
          Combined view: Stripe subscription + assigned phone + assistant coverage.
        </div>
      </header>
      {isLoading ? (
        <div className="p-6 text-center text-sm text-text-secondary">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="p-6 text-center text-sm text-text-secondary">
          No orgs found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
              <tr>
                <th className="px-4 py-2 text-left">Org</th>
                <th className="px-4 py-2 text-left">Stripe sub status</th>
                <th className="px-4 py-2 text-left">Phone</th>
                <th className="px-4 py-2 text-center">Inbound</th>
                <th className="px-4 py-2 text-center">Outbound</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map((row) => {
                const assigned = row.vapi_phone_number_id != null
                const isConfirming = confirmUnassign === row.org_id
                return (
                  <tr key={row.org_id}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{row.name}</div>
                      <div className="text-xs text-text-secondary">
                        {row.slug ?? '—'}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <SubStatusPill status={row.stripe_subscription_status} />
                    </td>
                    <td className="px-4 py-2">
                      {assigned ? (
                        <div>
                          <div className="font-mono text-xs">
                            {row.phone_e164 ?? '—'}
                          </div>
                          <div className="text-xs text-text-secondary">
                            {row.phone_label ?? ''}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-text-secondary">
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {row.has_inbound_assistant ? (
                        <span className="text-success">✓</span>
                      ) : (
                        <span className="text-text-secondary">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {row.has_outbound_assistant ? (
                        <span className="text-success">✓</span>
                      ) : (
                        <span className="text-text-secondary">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {assigned ? (
                        isConfirming ? (
                          <div className="inline-flex gap-1">
                            <button
                              type="button"
                              onClick={() => doUnassign(row.org_id)}
                              disabled={assignMutation.isPending}
                              className="rounded bg-error p-1 text-white hover:opacity-90 disabled:opacity-50"
                              title="Confirm unassign"
                            >
                              <Check className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmUnassign(null)}
                              className="rounded border border-border p-1 hover:bg-surface-elevated"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmUnassign(row.org_id)}
                            className="inline-flex items-center gap-1 text-xs text-error hover:underline"
                          >
                            <Trash2 className="h-3 w-3" /> Unassign
                          </button>
                        )
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAssigningOrg(row)}
                          className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs hover:bg-surface-elevated"
                        >
                          <Plus className="h-3 w-3" /> Assign
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {assignMutation.isError && confirmUnassign !== null && (
        <div className="border-t border-border px-4 py-2 text-xs text-error">
          Unassign failed: {String(assignMutation.error)}
        </div>
      )}

      {assigningOrg && (
        <AssignPhoneDialog
          org={assigningOrg}
          phones={phones}
          onClose={() => setAssigningOrg(null)}
        />
      )}
    </section>
  )
}

function SubStatusPill({ status }: { status: string | null }) {
  // Match the colour conventions used elsewhere in admin (active/trialing
  // green, past_due/unpaid amber, everything else neutral). Standalone so it
  // can be reused below the inventory table if we ever surface sub status there.
  if (!status)
    return (
      <span className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-xs text-text-secondary">
        none
      </span>
    )
  const ok = status === 'active' || status === 'trialing'
  const warn = status === 'past_due' || status === 'unpaid'
  const cls = ok
    ? 'border-success/30 bg-success/10 text-success'
    : warn
      ? 'border-warning/30 bg-warning/10 text-warning'
      : 'border-border bg-background text-text-secondary'
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${cls}`}
    >
      {status}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Section 2b — Assign-phone-to-org modal
// ─────────────────────────────────────────────────────────────────────────

function AssignPhoneDialog({
  org,
  phones,
  onClose,
}: {
  org: AdminOrgProvisioningRow
  phones: AdminPhoneNumber[]
  onClose: () => void
}) {
  // Only unassigned phones are eligible — the backend will 409 anything else,
  // and showing them here would invite the operator to click into a dead end.
  const unassigned = useMemo(
    () => phones.filter((p) => p.assigned_org_id == null),
    [phones],
  )
  const [pendingPhoneId, setPendingPhoneId] = useState<string>('')
  const assign = useAssignOrgPhone()
  const parsed = parsePhoneAssignError(assign.error)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!pendingPhoneId) return
    try {
      await assign.mutateAsync({ orgId: org.org_id, phone_id: pendingPhoneId })
      onClose()
    } catch {
      // surfaces below via parsed
    }
  }

  return (
    // Plain fixed-overlay; the codebase has no shared modal primitive.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="font-medium">Assign phone to {org.name}</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border p-1 hover:bg-surface-elevated"
          >
            <X className="h-3 w-3" />
          </button>
        </header>
        <form onSubmit={submit} className="space-y-3 p-4">
          {unassigned.length === 0 ? (
            <div className="rounded border border-warning/30 bg-warning/5 p-3 text-sm text-text-secondary">
              No unassigned phones available. Sync from VAPI or unassign one
              from another org first.
            </div>
          ) : (
            <label className="block space-y-1 text-sm">
              <span className="text-text-secondary">Phone</span>
              <select
                value={pendingPhoneId}
                onChange={(e) => setPendingPhoneId(e.target.value)}
                className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
              >
                <option value="">Pick a phone…</option>
                {unassigned.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.e164}
                    {p.label ? ` · ${p.label}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          {parsed?.status === 409 && (
            <div className="rounded border border-error/30 bg-error/5 p-2 text-xs text-error">
              {parsed.message ??
                'Phone is already assigned to another org. Unassign it there first.'}
              {parsed.conflict_org_id && (
                <div className="mt-1 font-mono text-text-secondary">
                  conflict_org_id: {parsed.conflict_org_id}
                </div>
              )}
            </div>
          )}
          {assign.isError && parsed?.status !== 409 && (
            <div className="rounded border border-error/30 bg-error/5 p-2 text-xs text-error">
              {String(assign.error)}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!pendingPhoneId || assign.isPending}
              className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" /> Assign
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Section 3 — VAPI phone inventory
// ─────────────────────────────────────────────────────────────────────────

function PhoneInventoryPanel({
  phones,
  isLoading,
}: {
  phones: AdminPhoneNumber[]
  isLoading: boolean
}) {
  const sync = useSyncPhoneNumbers()
  const [managePhoneId, setManagePhoneId] = useState<string | null>(null)
  // Newest sync time across the cache — gives the operator a quick "did sync
  // actually update anything?" signal even when nothing was upserted.
  const lastSyncedAt = useMemo(
    () =>
      phones.reduce<number>(
        (max, p) => (p.last_synced_at > max ? p.last_synced_at : max),
        0,
      ),
    [phones],
  )

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <div className="font-medium">VAPI phone inventory</div>
          {lastSyncedAt > 0 && (
            <div className="text-xs text-text-secondary">
              Last synced{' '}
              {new Date(lastSyncedAt * 1000)
                .toISOString()
                .replace('T', ' ')
                .slice(0, 19)}{' '}
              UTC
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="inline-flex items-center gap-1 rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated disabled:opacity-50"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${sync.isPending ? 'animate-spin' : ''}`}
          />
          {sync.isPending ? 'Syncing…' : 'Sync from VAPI'}
        </button>
      </header>

      {sync.isError && (
        <div className="border-b border-border px-4 py-2 text-xs text-error">
          Sync failed: {String(sync.error)}
        </div>
      )}

      {isLoading ? (
        <div className="p-6 text-center text-sm text-text-secondary">Loading…</div>
      ) : phones.length === 0 ? (
        <div className="p-6 text-center text-sm text-text-secondary">
          No cached phones yet — click <em>Sync from VAPI</em> to import.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
              <tr>
                <th className="px-4 py-2 text-left">e164</th>
                <th className="px-4 py-2 text-left">Label</th>
                <th className="px-4 py-2 text-left">Provider</th>
                <th className="px-4 py-2 text-left">Assigned to</th>
                <th className="px-4 py-2 text-center">Inbound / Outbound</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {phones.map((p) => {
                const inboundCount = p.assistants.filter(
                  (a) => a.kind === 'inbound' || a.kind === 'both',
                ).length
                const outboundCount = p.assistants.filter(
                  (a) => a.kind === 'outbound' || a.kind === 'both',
                ).length
                const isOpen = managePhoneId === p.id
                return (
                  <PhoneInventoryRow
                    key={p.id}
                    phone={p}
                    inboundCount={inboundCount}
                    outboundCount={outboundCount}
                    isOpen={isOpen}
                    onToggle={() =>
                      setManagePhoneId((curr) => (curr === p.id ? null : p.id))
                    }
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function PhoneInventoryRow({
  phone,
  inboundCount,
  outboundCount,
  isOpen,
  onToggle,
}: {
  phone: AdminPhoneNumber
  inboundCount: number
  outboundCount: number
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr>
        <td className="px-4 py-2 font-mono text-xs">{phone.e164}</td>
        <td className="px-4 py-2">{phone.label ?? '—'}</td>
        <td className="px-4 py-2 text-xs text-text-secondary">
          {phone.provider ?? '—'}
        </td>
        <td className="px-4 py-2">
          {phone.assigned_org_name ? (
            <div>
              <div className="text-sm">{phone.assigned_org_name}</div>
              <div className="text-xs text-text-secondary">
                {phone.assigned_org_slug ?? ''}
              </div>
            </div>
          ) : (
            <span className="text-xs text-text-secondary">—</span>
          )}
        </td>
        <td className="px-4 py-2 text-center text-xs">
          <span className="font-mono">
            {inboundCount} / {outboundCount}
          </span>
        </td>
        <td className="px-4 py-2 text-right">
          <button
            type="button"
            onClick={onToggle}
            className="rounded border border-border bg-background px-2 py-1 text-xs hover:bg-surface-elevated"
          >
            {isOpen ? 'Close' : 'Manage assistants'}
          </button>
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={6} className="bg-surface-elevated px-4 py-3">
            <ManageAssistantsEditor phone={phone} />
          </td>
        </tr>
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Section 3b — Manage assistants editor (inline, expanded under each row)
// ─────────────────────────────────────────────────────────────────────────

function ManageAssistantsEditor({ phone }: { phone: AdminPhoneNumber }) {
  const assistants = useAdminVapiAssistants()
  const add = useAddPhoneAssistant()
  const remove = useRemovePhoneAssistant()

  const [adding, setAdding] = useState(false)
  const [pendingAssistantId, setPendingAssistantId] = useState('')
  const [pendingKind, setPendingKind] = useState<PhoneAssistantKind>('inbound')

  async function doAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!pendingAssistantId) return
    const label =
      assistants.data?.assistants.find((a) => a.id === pendingAssistantId)
        ?.name ?? null
    try {
      await add.mutateAsync({
        phoneId: phone.id,
        assistant_id: pendingAssistantId,
        assistant_label: label,
        kind: pendingKind,
      })
      setAdding(false)
      setPendingAssistantId('')
      setPendingKind('inbound')
    } catch {
      // surfaces via add.error
    }
  }

  async function doRemove(assistant_id: string, kind: PhoneAssistantKind) {
    try {
      await remove.mutateAsync({
        phoneId: phone.id,
        assistant_id,
        kind,
      })
    } catch {
      // surfaces via remove.error
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-wide text-text-secondary">
        Assistant bindings
      </div>
      {phone.assistants.length === 0 ? (
        <div className="text-xs text-text-secondary">
          No assistants bound to this phone yet.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-text-secondary">
            <tr>
              <th className="px-2 py-1 text-left">Assistant</th>
              <th className="px-2 py-1 text-left">Kind</th>
              <th className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {phone.assistants.map((b) => (
              <tr key={`${b.assistant_id}:${b.kind}`}>
                <td className="px-2 py-1">
                  <div className="font-medium">
                    {b.assistant_label ?? '(unnamed)'}
                  </div>
                  <div className="font-mono text-xs text-text-secondary">
                    {b.assistant_id}
                  </div>
                </td>
                <td className="px-2 py-1">
                  <KindChip kind={b.kind} />
                </td>
                <td className="px-2 py-1 text-right">
                  <button
                    type="button"
                    onClick={() => doRemove(b.assistant_id, b.kind)}
                    disabled={remove.isPending}
                    className="inline-flex items-center gap-1 text-xs text-error hover:underline disabled:opacity-50"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!adding ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated"
        >
          <Plus className="h-3.5 w-3.5" /> Add binding
        </button>
      ) : (
        <form onSubmit={doAdd} className="flex flex-wrap items-center gap-2">
          <select
            value={pendingAssistantId}
            onChange={(e) => setPendingAssistantId(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="">
              {assistants.isLoading
                ? 'Loading assistants…'
                : 'Pick an assistant…'}
            </option>
            {(assistants.data?.assistants ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <select
            value={pendingKind}
            onChange={(e) =>
              setPendingKind(e.target.value as PhoneAssistantKind)
            }
            className="rounded border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="inbound">inbound</option>
            <option value="outbound">outbound</option>
            <option value="both">both</option>
          </select>
          <button
            type="submit"
            disabled={!pendingAssistantId || add.isPending}
            className="rounded bg-primary p-1.5 text-white hover:opacity-90 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false)
              setPendingAssistantId('')
              setPendingKind('inbound')
            }}
            className="rounded border border-border p-1.5 hover:bg-surface-elevated"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </form>
      )}

      {(add.isError || remove.isError) && (
        <div className="rounded border border-error/30 bg-error/5 p-2 text-xs text-error">
          {String(add.error ?? remove.error)}
        </div>
      )}
    </div>
  )
}

function KindChip({ kind }: { kind: PhoneAssistantKind }) {
  const cls =
    kind === 'inbound'
      ? 'border-primary/40 bg-primary/10 text-primary'
      : kind === 'outbound'
        ? 'border-success/40 bg-success/10 text-success'
        : 'border-border bg-background text-text-secondary'
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${cls}`}
    >
      {kind}
    </span>
  )
}
